import React, { useReducer, useEffect } from 'react';
import { Col, Row, Slider, Divider } from 'antd';

import { DEFAULT_SHADOW_BORDER_COLOR } from 'constants/colors';
import HueSlider from '../HueSlider/HueSlider';

const initialState = {
  xOffset: 0,
  yOffset: 0,
  blur: 0,
  spread: 0,
  hueColor: { ...DEFAULT_SHADOW_BORDER_COLOR },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setXOffset':
      return { ...state, xOffset: action.xOffset };
    case 'setYOffset':
      return { ...state, yOffset: action.yOffset };
    case 'setBlur':
      return { ...state, blur: action.blur };
    case 'setSpread':
      return { ...state, spread: action.spread };
    case 'setHueColor':
      return { ...state, hueColor: action.hueColor };
    case 'reset':
      return initialState;
    default:
      throw new Error('Unexpected action');
  }
};

const ShadowPicker = ({ shadow, onChange }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (shadow) {
      dispatch({ type: 'setXOffset', xOffset: shadow?.xOffset });
      dispatch({ type: 'setYOffset', yOffset: shadow?.yOffset });
      dispatch({ type: 'setBlur', blur: shadow?.blur });
      dispatch({ type: 'setSpread', spread: shadow?.spread });
      dispatch({ type: 'setHueColor', hueColor: shadow?.hueColor });
    }
  }, []); //eslint-disable-line

  const handleChange = (value, dispatchType, stateType) => {
    dispatch({ type: dispatchType, [stateType]: value });
    onChange({ ...state, [stateType]: value });
  };

  const handleAlpha = (alpha) => {
    // update the alpha value in hueColor's rgb
    let value = { ...state?.hueColor };

    if (!value?.rgb) {
      value = { ...state?.hueColor, rgb: { ...initialState.hueColor.rgb } };
    }

    value.rgb.a = alpha;
    handleChange(value, 'setHueColor', 'hueColor');
  };

  return (
    <Row className="shadow-picker" gutter={16}>
      <Col span={24}>
        <h4>Shadow</h4>
      </Col>

      <Col className="gutter-row" span={12}>
        <small className="image-crop-label">X offset {state.xOffset}</small>
        <Slider
          step={1}
          min={-20}
          max={20}
          value={state.xOffset}
          onChange={(value) => handleChange(value, 'setXOffset', 'xOffset')}
          tooltipVisible={false}
        />
      </Col>
      <Col className="gutter-row" span={12}>
        <small className="image-crop-label">Y offset {state.yOffset}</small>
        <Slider
          step={1}
          min={-20}
          max={20}
          value={state.yOffset}
          onChange={(value) => handleChange(value, 'setYOffset', 'yOffset')}
          tooltipVisible={false}
        />
      </Col>
      <Divider />
      <Col className="gutter-row" span={12}>
        <small className="image-crop-label">Blur {state.blur}</small>
        <Slider
          step={1}
          min={0}
          max={20}
          value={state.blur}
          onChange={(value) => handleChange(value, 'setBlur', 'blur')}
          tooltipVisible={false}
        />
      </Col>
      <Col className="gutter-row" span={12}>
        <small className="image-crop-label">Spread {state.spread}</small>
        <Slider
          step={1}
          min={0}
          max={20}
          value={state.spread}
          onChange={(value) => handleChange(value, 'setSpread', 'spread')}
          tooltipVisible={false}
        />
      </Col>
      <Divider />
      <Col className="gutter-row" span={24}>
        <HueSlider
          value={state?.hueColor}
          onChange={(value) => handleChange(value, 'setHueColor', 'hueColor')}
        />
      </Col>
      <Col className="gutter-row" span={24}>
        <div className="custom-color-slider">
          <small className="image-crop-label">Transparency {state?.hueColor?.rgb?.a}</small>
          <div className="alpha-picker">
            <Slider
              step={0.1}
              min={0}
              max={1}
              value={state?.hueColor?.rgb?.a ?? 1}
              onChange={handleAlpha}
              tooltipVisible={false}
              style={{
                background: `linear-gradient(to right, transparent, ${
                  state?.hueColor?.hex || '#000'
                })`,
              }}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default ShadowPicker;
